/**
 * The Conductor
 * -------------
 * Reads FINISHED runs and proposes durable fixes. It never takes part in a run.
 *
 * That boundary is not a stylistic choice. The engine deliberately declines AI
 * on api, session, live and audit runs so that a broken live template FAILS
 * and says so rather than being silently repaired — those runs are production
 * monitors and their honesty is the point. A conductor that patched a live run
 * would destroy exactly that guarantee. So it runs afterwards, out of band, and
 * its output is a PROPOSAL that reaches production the same way any other
 * template change does.
 *
 * It is a batch reader on purpose. One failed run rarely says what is wrong;
 * fifteen failed runs for the same merchant, all dying at the same stage, very
 * often do. Fixing the cause once is worth more than reacting fifteen times.
 *
 * The goal is to put itself out of a job on a merchant: every proposal should
 * prefer a DETERMINISTIC artifact — a corrected URL, a pinned selector, a
 * routing rule — over anything that needs a model again next time. Proposals
 * per merchant should trend to zero. If they do not, the conductor is papering
 * over a defect rather than fixing one.
 */

/** What a proposal asks for. A closed set: anything else is rejected, not stored. */
export enum ConductorProposalType {
  /** Drop a cached product URL that is dead or was never a product page. */
  RetireProductUrl = "retire_product_url",
  /** Cache a product URL the evidence shows is testable. */
  AddProductUrl = "add_product_url",
  /** Correct `additionalSettings.knownCartPage`. */
  SetCartUrl = "set_cart_url",
  /** Replace one selector on the merchant map. */
  ReplaceSelector = "replace_selector",
  /**
   * This merchant cannot be automated by any path we have. Terminal, and the
   * most valuable output when it is true — it stops the estate spending runs
   * on something no fix will reach.
   */
  NotAutomatable = "not_automatable",
  /**
   * Nothing to do. Recorded rather than returned empty so the same evidence is
   * not re-examined on every sweep.
   */
  NoAction = "no_action",
}

export enum ConductorProposalStatus {
  /** Written, not yet acted on. Nothing reads these into a run. */
  Pending = "pending",
  /** A later run or a person applied it. */
  Applied = "applied",
  /** Applied AND an independent run since succeeded — promotable. */
  Confirmed = "confirmed",
  /** Applied and the next run failed the same way. */
  Refuted = "refuted",
  /** A person declined it. */
  Rejected = "rejected",
}

export interface IConductorProposal {
  _id?: string;
  merchantId: string;
  domain: string;
  countryCode: string;
  type: ConductorProposalType;
  /** Dotted merchant-map path for ReplaceSelector, e.g. "coupon.couponBoxSelector". */
  field?: string | null;
  /** What is stored today. Kept so a proposal can be reviewed, and reversed. */
  currentValue?: string | null;
  /** What the conductor proposes instead. Null for NotAutomatable / NoAction. */
  proposedValue?: string | null;
  /** Why, in the conductor's own words. Shown to whoever reviews it. */
  rationale: string;
  /** 0..1, self-reported. Treated as ordering only, never as a licence to apply. */
  confidence: number;
  status: ConductorProposalStatus;

  // ── provenance: what it read, what it cost, what produced it ──────────────
  /** Task ids the dossier was built from. The evidence, re-readable later. */
  evidenceTaskIds: string[];
  /** The failure stage these runs shared — why the merchant was picked. */
  observedFailureStage?: string | null;
  /** Named `modelName`, not `model` — `model` collides with mongoose Document. */
  modelName: string;
  costUsd: number;
  /** Bumped when the prompt or dossier shape changes, so old rows stay readable. */
  conductorVersion: number;

  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * One merchant's evidence, as handed to the model.
 *
 * Deliberately small and structured. A raw DOM would blow past the 272k
 * long-context cliff (which reprices the WHOLE request at 2x input / 1.5x
 * output), and the question here is not "what is on this page" — it is "what
 * do fifteen failures have in common".
 */
export interface IConductorDossier {
  merchantId: string;
  domain: string;
  countryCode: string;
  checkoutType?: string | null;
  merchantStatus?: string | null;
  /** The stage these runs shared. */
  failureStage: string;
  failureOwner?: string | null;
  runs: Array<{
    taskId: string;
    at: string;
    executionMode?: string | null;
    errorType?: string | null;
    errorMessage?: string | null;
    deepestState?: string | null;
    /** The last few log lines before it stopped. */
    tail: string[];
  }>;
  route: {
    cachedProductUrls: Array<{ url: string; ok: number; failed: number }>;
    knownCartPage?: string | null;
    hasSession: boolean;
    couponSelectors: Record<string, string | null>;
  };
}
